import { useState, useEffect, useCallback } from 'react';
import { agendaService, Event, Calendar, EventStats, Notification, AgendaSettings } from '../services/agendaService';

interface UseAgendaReturn {
  // Estado
  events: Event[];
  calendars: Calendar[];
  notifications: Notification[];
  settings: AgendaSettings | null;
  stats: EventStats | null;
  loading: boolean;
  error: string | null;

  // Ações de eventos
  loadEvents: (filters?: any) => Promise<void>;
  createEvent: (eventData: any) => Promise<void>;
  updateEvent: (id: string, eventData: any) => Promise<void>;
  deleteEvent: (id: string) => Promise<void>;
  searchEvents: (query: string, filters?: any) => Promise<void>;

  // Ações de calendários
  loadCalendars: () => Promise<void>;
  createCalendar: (calendarData: any) => Promise<void>;
  updateCalendar: (id: string, calendarData: any) => Promise<void>;
  deleteCalendar: (id: string) => Promise<void>;

  // Ações de notificações
  loadNotifications: (unreadOnly?: boolean) => Promise<void>;
  markNotificationAsRead: (id: string) => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;

  // Ações de configurações
  loadSettings: () => Promise<void>;
  updateSettings: (settings: any) => Promise<void>;

  // Ações de estatísticas
  loadStats: () => Promise<void>;

  // Utilitários
  clearError: () => void;
  refreshData: () => Promise<void>;
}

// Função para obter data de São Paulo (mesma do Agenda.tsx)
const getSaoPauloDate = (dateString?: string): Date => {
  if (dateString) {
    // Se recebeu uma string, converter para Date no timezone de São Paulo
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  } else {
    // Se não recebeu string, usar data atual de São Paulo
    const now = new Date();
    const saoPauloOffset = -3 * 60; // -3 hours in minutes
    const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
    const saoPauloTime = new Date(utc + (saoPauloOffset * 60000));
    return new Date(saoPauloTime.getFullYear(), saoPauloTime.getMonth(), saoPauloTime.getDate());
  }
};

// Função para converter eventos do backend para o formato do frontend
const convertBackendEventToLocal = (backendEvent: any): Event => {
  // Corrigir problema de timezone: converter string YYYY-MM-DD para Date local
  let eventDate: Date;
  if (typeof backendEvent.event_date === 'string') {
    const [year, month, day] = backendEvent.event_date.split('-').map(Number);
    eventDate = new Date(year, month - 1, day);
  } else {
    eventDate = backendEvent.event_date;
  }

  return {
    ...backendEvent,
    date: eventDate
  };
};

export const useAgenda = (): UseAgendaReturn => {
  // Estado
  const [events, setEvents] = useState<Event[]>([]);
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [settings, setSettings] = useState<AgendaSettings | null>(null);
  const [stats, setStats] = useState<EventStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Função para tratar erros
  const handleError = useCallback((err: any) => {
    console.error('Agenda service error:', err);
    setError(err.message || 'Erro ao carregar dados da agenda');
    setLoading(false);
  }, []);

  // Função para limpar erro
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // =====================================================
  // EVENTOS
  // =====================================================

  const loadEvents = useCallback(async (filters?: any) => {
    try {
      setLoading(true);
      setError(null);
      const response = await agendaService.getEvents(filters);
      if (response.success) {
        // Converter eventos do backend para o formato do frontend
        const convertedEvents = response.data.map(convertBackendEventToLocal);
        setEvents(convertedEvents);
      } else {
        throw new Error('Falha ao carregar eventos');
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const createEvent = useCallback(async (eventData: any) => {
    try {
      setLoading(true);
      setError(null);
      const response = await agendaService.createEvent(eventData);
      if (response.success) {
        // Recarregar eventos após criar
        await loadEvents();
        // Recarregar estatísticas
        await loadStats();
      } else {
        throw new Error('Falha ao criar evento');
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [loadEvents, handleError]);

  const updateEvent = useCallback(async (id: string, eventData: any) => {
    try {
      setLoading(true);
      setError(null);
      const response = await agendaService.updateEvent(id, eventData);
      if (response.success) {
        // Atualizar evento na lista local
        setEvents(prev => prev.map(event => 
          event.id === id ? { ...event, ...response.data } : event
        ));
        // Recarregar estatísticas
        await loadStats();
      } else {
        throw new Error('Falha ao atualizar evento');
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const deleteEvent = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await agendaService.deleteEvent(id);
      if (response.success) {
        // Remover evento da lista local
        setEvents(prev => prev.filter(event => event.id !== id));
        // Recarregar estatísticas
        await loadStats();
      } else {
        throw new Error('Falha ao excluir evento');
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const searchEvents = useCallback(async (query: string, filters?: any) => {
    try {
      setLoading(true);
      setError(null);
      const response = await agendaService.searchEvents(query, filters);
      if (response.success) {
        setEvents(response.data);
      } else {
        throw new Error('Falha ao buscar eventos');
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  // =====================================================
  // CALENDÁRIOS
  // =====================================================

  const loadCalendars = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await agendaService.getCalendars();
      if (response.success) {
        setCalendars(response.data);
      } else {
        throw new Error('Falha ao carregar calendários');
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const createCalendar = useCallback(async (calendarData: any) => {
    try {
      setLoading(true);
      setError(null);
      const response = await agendaService.createCalendar(calendarData);
      if (response.success) {
        // Adicionar calendário à lista local
        setCalendars(prev => [...prev, response.data]);
      } else {
        throw new Error('Falha ao criar calendário');
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const updateCalendar = useCallback(async (id: string, calendarData: any) => {
    try {
      setLoading(true);
      setError(null);
      const response = await agendaService.updateCalendar(id, calendarData);
      if (response.success) {
        // Atualizar calendário na lista local
        setCalendars(prev => prev.map(calendar => 
          calendar.id === id ? { ...calendar, ...response.data } : calendar
        ));
      } else {
        throw new Error('Falha ao atualizar calendário');
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const deleteCalendar = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await agendaService.deleteCalendar(id);
      if (response.success) {
        // Remover calendário da lista local
        setCalendars(prev => prev.filter(calendar => calendar.id !== id));
      } else {
        throw new Error('Falha ao excluir calendário');
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  // =====================================================
  // NOTIFICAÇÕES
  // =====================================================

  const loadNotifications = useCallback(async (unreadOnly: boolean = false) => {
    try {
      setLoading(true);
      setError(null);
      const response = await agendaService.getNotifications(unreadOnly);
      if (response.success) {
        setNotifications(response.data);
      } else {
        throw new Error('Falha ao carregar notificações');
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const markNotificationAsRead = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await agendaService.markNotificationAsRead(id);
      if (response.success) {
        // Atualizar notificação na lista local
        setNotifications(prev => prev.map(notification => 
          notification.id === id ? { ...notification, is_read: true } : notification
        ));
      } else {
        throw new Error('Falha ao marcar notificação como lida');
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const deleteNotification = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await agendaService.deleteNotification(id);
      if (response.success) {
        // Remover notificação da lista local
        setNotifications(prev => prev.filter(notification => notification.id !== id));
      } else {
        throw new Error('Falha ao excluir notificação');
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  // =====================================================
  // CONFIGURAÇÕES
  // =====================================================

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await agendaService.getSettings();
      if (response.success) {
        setSettings(response.data);
      } else {
        throw new Error('Falha ao carregar configurações');
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  const updateSettings = useCallback(async (settingsData: any) => {
    try {
      setLoading(true);
      setError(null);
      const response = await agendaService.updateSettings(settingsData);
      if (response.success) {
        setSettings(response.data);
      } else {
        throw new Error('Falha ao atualizar configurações');
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  // =====================================================
  // ESTATÍSTICAS
  // =====================================================

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await agendaService.getEventStats();
      if (response.success) {
        setStats(response.data);
      } else {
        throw new Error('Falha ao carregar estatísticas');
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  // =====================================================
  // UTILITÁRIOS
  // =====================================================

  const refreshData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([
        loadEvents(),
        loadCalendars(),
        loadNotifications(),
        loadSettings(),
        loadStats()
      ]);
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [loadEvents, loadCalendars, loadNotifications, loadSettings, loadStats, handleError]);

  // Carregar dados iniciais
  useEffect(() => {
    refreshData();
  }, [refreshData]);

  return {
    // Estado
    events,
    calendars,
    notifications,
    settings,
    stats,
    loading,
    error,

    // Ações de eventos
    loadEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    searchEvents,

    // Ações de calendários
    loadCalendars,
    createCalendar,
    updateCalendar,
    deleteCalendar,

    // Ações de notificações
    loadNotifications,
    markNotificationAsRead,
    deleteNotification,

    // Ações de configurações
    loadSettings,
    updateSettings,

    // Ações de estatísticas
    loadStats,

    // Utilitários
    clearError,
    refreshData
  };
};

export default useAgenda;

